import React from "react";

export const SUPPORT_CONTACT = "support@1099policy.com";

// The ways the card step can fail. They need different advice, and only the
// two DECLINED kinds are the card issuer saying no.
export const PaymentFailureKind = {
  // saveAndStepForward raised PAYMENT_NOT_AUTHORIZED: the backend asked for an
  // authorization (final_review_step) and the issuer declined it, for a
  // reason that may clear (insufficient funds, do not honour...).
  DECLINED: "declined",
  // As DECLINED, but a decline that can never succeed on this card (lost,
  // stolen, expired, invalid account). The API reports `retriable: false`.
  HARD_DECLINED: "hard_declined",
  // The API refused to authorize this card again because its last charge was
  // a hard decline (`attempted: false`). Nothing reached the issuer.
  REFUSED_HARD_DECLINED_CARD: "refused_hard_declined_card",
  // The card never reached the issuer: Checkout tokenization failed, or
  // storeCheckoutInstrument errored or returned ok: false. That is a problem
  // on our side or Checkout's, so the contractor's bank can't fix it.
  PROCESSING: "processing",
};

// Only issuer declines count. A tokenize or store failure is not evidence
// that anything is wrong with the card, and a refused re-authorization made no
// new attempt, so neither may move the contractor toward the repeated-decline
// message.
export const countsAsDecline = (kind) =>
  kind === PaymentFailureKind.DECLINED ||
  kind === PaymentFailureKind.HARD_DECLINED;

// Which kind a PAYMENT_NOT_AUTHORIZED is, from the error's `extensions`. An
// API that predates `retriable` / `attempted` sends neither, and every decline
// then reads as DECLINED, which is what this page showed before.
export const declineKindFor = (extensions) => {
  if (extensions?.attempted === false) {
    return PaymentFailureKind.REFUSED_HARD_DECLINED_CARD;
  }
  if (extensions?.retriable === false) return PaymentFailureKind.HARD_DECLINED;
  return PaymentFailureKind.DECLINED;
};

// From this many declines in a session the message stops reading like a
// one-off and explains where declines come from. It never disables the form:
// the session-storage lock that used to sit here sent contractors to support,
// who cannot override an issuer decline.
export const REPEATED_DECLINE_THRESHOLD = 3;

// The backend sends str(response_summary), which is the string "None" when
// Checkout returns no summary (e.g. a pending 3DS response).
const displayableReason = (reason) =>
  reason && reason !== "None" ? reason : null;

export const paymentFailureMessage = (paymentError, declineCount) => {
  if (paymentError.kind === PaymentFailureKind.PROCESSING) {
    return (
      "We couldn't save your card details. Your card wasn't declined, so " +
      "please check the card number, expiry date and security code and try " +
      `again. If this keeps happening, email ${SUPPORT_CONTACT}.`
    );
  }

  // A refusal carries the API's own wording ("This card was declined by its
  // issuer"), not a decline reason, so it is not repeated in brackets.
  const reason =
    paymentError.kind === PaymentFailureKind.REFUSED_HARD_DECLINED_CARD
      ? null
      : displayableReason(paymentError.reason);
  if (
    paymentError.kind === PaymentFailureKind.HARD_DECLINED ||
    paymentError.kind === PaymentFailureKind.REFUSED_HARD_DECLINED_CARD
  ) {
    // Neither the bank nor another try can make this card work, so the only
    // advice is a different card.
    return (
      "This card can't be used" +
      (reason ? ` (${reason})` : "") +
      ", and trying it again won't work. Please add a different card."
    );
  }
  if (declineCount >= REPEATED_DECLINE_THRESHOLD) {
    return (
      `Your payment has been declined ${declineCount} times` +
      (reason ? ` (${reason})` : "") +
      ". Declines usually come from your bank, so call the number on the " +
      "back of your card and ask them to allow the charge, or try a " +
      `different card. If more than one card is declined, email ${SUPPORT_CONTACT}.`
    );
  }
  return (
    "Your card was declined" +
    (reason ? ` (${reason})` : "") +
    ". Try a different card, or call the number on the back of your card " +
    "and ask your bank to allow the charge."
  );
};

export const PaymentFailureNotice = ({ paymentError, declineCount }) => {
  if (!paymentError) return null;
  return (
    <div role="alert" className="flex items-center justify-center dark-red f7 mb3">
      <span className="lh-title ml3-l bg-washed-red ph2 ph4-l pv2 br2">
        {paymentFailureMessage(paymentError, declineCount)}
      </span>
    </div>
  );
};
